import { useTranslation } from "react-i18next";
import {
  Invitation,
  Organization,
} from "../../../services/organization/organizationModel";
import { useRouteLoaderData } from "react-router-dom";
import {
  compareString,
  useSearch,
  useTableSort,
} from "../../../react-helpers/array";
import { useMemo, useState } from "react";
import UsersOverview from "../UsersOverview";
import InviteAndManageUserDialog from "./InviteAndManageUserDialog";
import { User } from "../../../services/user/userModel";
import { randomUUID } from "../../../react-helpers/crypto";
import {
  getOrganizationMemberStatus,
  ORGANIZATION_MEMBER_STATUS_LABEL_KEYS,
  ORGANIZATION_MEMBER_STATUSES,
  OrganizationMemberStatus,
} from "../../../services/organization/organizationService";
import UsersTable from "./UsersTable";

// Sorting the status column on its meaning rather than on the translated
// label: from the most to the least active membership.
const STATUS_RANK: Record<OrganizationMemberStatus, number> = {
  active: 0,
  pending: 1,
  disabled: 2,
};

const UsersPage = () => {
  const { t } = useTranslation(["organization"]);
  const { organization } = useRouteLoaderData("organization") as {
    organization: Organization;
  };

  const users = useMemo(() => {
    const mergedUsers: Record<
      string | number,
      User & { invitation?: Invitation }
    > = {};

    organization.users?.forEach((user) => {
      mergedUsers[user.id] = user;
    });

    organization.invitations?.forEach((invitation) => {
      if (invitation.userId && invitation.user) {
        if (!mergedUsers[invitation.userId]) {
          // User is not in the orgs users but has a user account so it's an INVITED
          mergedUsers[invitation.userId] = {
            ...invitation.user,
            invitation,
            adminOrga: false,
            organizationMembershipStatus: invitation.disabledDate
              ? "disabled"
              : "active",
          };
        } else {
          // Do nothing user already in organization.users
        }
      } else {
        // User does not have an account it's pending
        const id = randomUUID() as any;
        mergedUsers[id] = {
          ...invitation,
          id,
          invitation,
          adminOrga: false,
        };
      }
    });

    return Object.values(mergedUsers);
  }, [organization.users, organization.invitations]);

  const [searchedUsers, setSearch] = useSearch(users ?? []);

  const [statusFilter, setStatusFilter] = useState<
    OrganizationMemberStatus | "all"
  >("all");

  const filteredUsers = useMemo(
    () =>
      statusFilter === "all"
        ? searchedUsers
        : searchedUsers.filter(
            (user) =>
              getOrganizationMemberStatus(organization.id, user) ===
              statusFilter,
          ),
    [searchedUsers, statusFilter, organization.id],
  );

  const comparators = useMemo(
    () => ({
      name: (a: (typeof users)[number], b: (typeof users)[number]) =>
        compareString(
          `${a.lastname} ${a.firstname}`,
          `${b.lastname} ${b.firstname}`,
        ),
      status: (a: (typeof users)[number], b: (typeof users)[number]) =>
        STATUS_RANK[
          getOrganizationMemberStatus(organization.id, a) ?? "active"
        ] -
        STATUS_RANK[
          getOrganizationMemberStatus(organization.id, b) ?? "active"
        ],
    }),
    [organization.id],
  );

  const [sortedUsers, sort, toggleSort] = useTableSort(
    filteredUsers,
    comparators,
  );

  const [inviteAndManageUser, setInviteAndManageUser] = useState<{
    user?: User;
    invitation?: Invitation;
  } | null>(null);

  const memberCount = useMemo(
    () =>
      users.reduce(
        (acc, user) =>
          getOrganizationMemberStatus(organization.id, user) !== "disabled"
            ? acc + 1
            : acc,
        0,
      ) ?? 0,
    [users, organization.id],
  );

  // Counts shown in the status filter, over the whole list (not the search).
  const statusCounts = useMemo(() => {
    const counts: Record<OrganizationMemberStatus, number> = {
      active: 0,
      pending: 0,
      disabled: 0,
    };
    users.forEach((user) => {
      const status = getOrganizationMemberStatus(organization.id, user);
      if (status) counts[status] += 1;
    });
    return counts;
  }, [users, organization.id]);

  const seatsLeft = useMemo(() => {
    return Math.max(organization.maxMembersCount - memberCount, 0);
  }, [organization.maxMembersCount, memberCount]);

  return (
    <div className="layout_content --bg--organization-dashboard">
      <div className="ui-page">
        <div className="ui-ph">
          <div className="ui-ph_t">
            <h1>{t("organization:users.PAGE_TITLE")}</h1>
          </div>
        </div>

        <UsersOverview
          organization={organization}
          users={organization.users ?? []}
          invitations={organization.invitations ?? []}
          onNewUser={() => {
            setInviteAndManageUser({});
          }}
        />

        <div className="admin-toolbar">
          <label className="admin-search">
            <span className="admin-ic --search" aria-hidden="true" />
            <input
              type="text"
              className="input"
              aria-label={t("organization:users.SEARCH_USERS")}
              placeholder={t("organization:users.SEARCH_USERS")}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <select
            className="select admin-toolbar_filter"
            aria-label={t("organization:users.FILTER_BY_STATUS")}
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(
                e.target.value as OrganizationMemberStatus | "all",
              )
            }
          >
            <option value="all">
              {t("organization:users.ALL_STATUSES")} ({users.length})
            </option>
            {ORGANIZATION_MEMBER_STATUSES.map((status) => (
              <option key={`status-${status}`} value={status}>
                {t(ORGANIZATION_MEMBER_STATUS_LABEL_KEYS[status])} (
                {statusCounts[status]})
              </option>
            ))}
          </select>
        </div>

        <UsersTable
          users={sortedUsers}
          organization={organization}
          setInviteAndManageUser={setInviteAndManageUser}
          sort={sort}
          onSort={toggleSort}
        />
      </div>

      {inviteAndManageUser && (
        <InviteAndManageUserDialog
          onClose={() => setInviteAndManageUser(null)}
          user={inviteAndManageUser.user}
          invitation={inviteAndManageUser.invitation}
          organization={organization}
          seatsLeft={seatsLeft}
        />
      )}
    </div>
  );
};

export default UsersPage;
